/* eslint-disable css-modules/no-unused-class */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cn from 'classnames';
import sanitizer from 'sanitizer';
import { Link } from 'react-router';
import appendQuery from 'append-query';
import { deepEqual } from 'fast-equals';

import { SEARCH_PAGE } from 'constants/amethystPageTypes';
import { BEST_FOR_YOU_FACETFIELD } from 'constants/appConstants';
import {
  SAVED_SEARCH_COOKIE,
  SAVI_COOKIE,
  SAVI_SIZE_COOKIE,
  SEARCH_BEST_FOR_YOU_COOKIE,
  SEARCH_COLOR_SWATCHES_COOKIE,
  SEARCH_LAYOUT_COOKIE
} from 'constants/cookies';
import {
  HYDRA_6PM_DISPLAY_ADS,
  HYDRA_CROSS_SITE_SELLING,
  HYDRA_NEW_ADS,
  HYDRA_PDP_PRODUCT_ANGLES,
  HYDRA_PLACEBO_SEARCH,
  HYDRA_SAVE_AND_APPLY_FILTERS,
  HYDRA_SAVE_SEARCHES,
  HYDRA_SEARCH_COLOR_SWATCHES,
  HYDRA_SEARCH_INLINE_VERTICAL_RECOS,
  HYDRA_SEARCH_SIZE_COLLAPSE,
  HYDRA_SEARCH_SPELLCHECK,
  HYDRA_SEO_H1_TITLE_COPY,
  HYDRA_SOCIAL_COLLECTIONS,
  HYDRA_VISUALLY_ADJUSTED_ADS
} from 'constants/hydraTests';
import {
  SEO_COPY_BOTTOM_COLLAPSED,
  SEO_COPY_BOTTOM_EXPANDED,
  SEO_COPY_TOP_COLLAPSED
} from 'constants/seoOptimizedDataConstants';
import { evSortSearchClick } from 'events/search';
import marketplace from 'cfg/marketplace.json';
// constants
// actions
import { deleteSavedFilters, saveFilters } from 'actions/fancyUrls';
import { clearAutoComplete } from 'actions/autoComplete';
import { redirectTo, redirectToAuthenticationFor } from 'actions/redirect';
import { removeFromStoredCookies } from 'actions/session';
import { heartProduct, toggleHeartingLoginModal, unHeartProduct } from 'actions/hearts';
import { getAssignmentGroup, isAssigned, isInAssignment, triggerAssignment } from 'actions/ab';
import { deleteSavedSearch, saveSearch, searchFeedbackClick, toggleFacetsContainer, toggleSavedFilters, toggleSavedSearch, updateBestForYou, updateSearchLayout, updateSort } from 'actions/search';
import { removePersonalizedSize, resetFacetGroup, toggleFacetChosen, togglePersonalizedSize, toggleSelectedFacet, toggleSelectedFacetGroup } from 'actions/facets';
import { trackSponsoredProductEvent } from 'store/ducks/search/actions';
// resources
import { track } from 'apis/amethyst';
import { ensureClass, removeCookie, sanitizeForEvent, setCookie } from 'helpers';
import {
  fireApsAds,
  SRP_NARROW_MID,
  SRP_NARROW_TOP,
  SRP_WIDE_MID,
  SRP_WIDE_TOP
} from 'helpers/apsAdvertisement';
import { isNotBot } from 'helpers/ClientUtils';
import { onEvent } from 'helpers/EventHelpers';
import { getHeartProps } from 'helpers/HeartUtils';
import { parseAndCleanHtmlToReact } from 'helpers/HtmlHelpers';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { buildSearchTermEventPayload, formatProductClickEvent, formatSavedFilters, sortStringToObject, urlMatchesSavedSearch } from 'helpers/SearchUtils';
// containers/components
import JanusPixel from 'components/common/JanusPixel';
import GamSlot from 'components/common/GamSlot';
import AdsenseSlot from 'components/common/AdsenseSlot';
import { isValidLayout } from 'components/search/Layout';
import DesktopSearchHeader from 'components/search/DesktopSearchHeader';
import DesktopSearchMenu from 'components/search/DesktopSearchMenu';
import Facets from 'components/search/Facets';
import FacetActions from 'components/search/FacetActions';
import FacetMenu from 'components/search/FacetMenu';
import Pagination from 'components/common/Pagination';
import SearchSlot from 'components/search/SearchSlot';
import SearchLogic from 'containers/SearchLogic';
import Products from 'components/search/Products';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import SkipLinks from 'components/common/SkipLinks';
import { MoreInfo } from 'components/icons';
import Tooltip from 'components/common/Tooltip';

import css from 'styles/containers/search.scss';

// marketplace
const {
  cookieDomain,
  features: {
    showRatings
  },
  hasHearting,
  search: {
    autoCompleteMinValues,
    facetHeader,
    hasFacetListClear,
    mobileCloseFiltersText,
    mobileFacetHeader,
    mobileRemoveSelectionsText,
    msaMelodyImageParams,
    showRatingStars,
    showSeoText,
    sortOptions
  }
} = marketplace;

const isInColorSwatchTest = state => Boolean(getAssignmentGroup(HYDRA_SEARCH_COLOR_SWATCHES, state)) && state.cookies[SEARCH_COLOR_SWATCHES_COOKIE] !== 'off';

export class StandardSearch extends Component {

  static fetchDataOnServer(store, location, params, fetchParams = {}) {
    const state = store.getState();
    const { cookies } = state;
    const hydraSaveFilters = isAssigned(HYDRA_SAVE_AND_APPLY_FILTERS, 1, state);

    if (isInColorSwatchTest(state) && !location.search.includes('plsr=1')) {
      // cut out early if we need the plsr=1 param still. we will make our call when we do have it.
      return;
    }

    if (hydraSaveFilters && !cookies?.[SAVI_COOKIE]) {
      store.dispatch(toggleSavedFilters());
    }

    return SearchLogic.fetchDataOnServer(store, location, params, fetchParams);
  }

  static afterFetchDataOnServer(store) {
    return SearchLogic.afterFetchDataOnServer(store);
  }

  static beforeFetchDataOnServer({ dispatch, getState }, { pathname, search }) {
    const { url: { userAgent } } = getState();
    dispatch(triggerAssignment(HYDRA_PDP_PRODUCT_ANGLES));
    dispatch(triggerAssignment(HYDRA_SEO_H1_TITLE_COPY));
    dispatch(triggerAssignment(HYDRA_SOCIAL_COLLECTIONS));
    dispatch(triggerAssignment(HYDRA_SAVE_AND_APPLY_FILTERS));
    dispatch(triggerAssignment(HYDRA_SEARCH_SIZE_COLLAPSE));
    if (isNotBot(userAgent)) {
      dispatch(triggerAssignment(HYDRA_SEARCH_SPELLCHECK));
      dispatch(triggerAssignment(HYDRA_SEARCH_COLOR_SWATCHES));
      if (isInColorSwatchTest(getState()) && !search.includes('plsr=1')) {
        dispatch(redirectTo(appendQuery(`${pathname}${search}`, 'plsr=1')));
      }
    }
  }

  state = {
    stickyHeaderTop: 0,
    seoCopyCollapsed: true,
    feedbackSubmitted: false
  };

  componentDidMount() {
    const { cookieDomain, location, removeFromStoredCookies, saveFilters, saveSearch, saviSizeCookie, triggerAssignment, userAgent, isCustomer, hydraSaveFilters, hydraSavedSearches, savedSearchCookie, savedSearches, toggleSavedSearch } = this.props;
    triggerAssignment(HYDRA_SEARCH_INLINE_VERTICAL_RECOS);
    triggerAssignment(HYDRA_PLACEBO_SEARCH);
    triggerAssignment(HYDRA_PDP_PRODUCT_ANGLES);
    triggerAssignment(HYDRA_SEO_H1_TITLE_COPY);
    triggerAssignment(HYDRA_SOCIAL_COLLECTIONS);
    triggerAssignment(HYDRA_SAVE_AND_APPLY_FILTERS);
    triggerAssignment(HYDRA_SEARCH_SIZE_COLLAPSE);
    const hydra6pmDisplayAds = triggerAssignment(HYDRA_6PM_DISPLAY_ADS);
    if (isNotBot(userAgent)) {
      triggerAssignment(HYDRA_SEARCH_SPELLCHECK);
      triggerAssignment(HYDRA_SEARCH_COLOR_SWATCHES);
    }

    this.plsrSeoRedirect();

    if (hydraSaveFilters && saviSizeCookie && isCustomer) {
      removeCookie(SAVI_SIZE_COOKIE, cookieDomain);
      saveFilters(null, true);
    }

    if (isInAssignment(hydra6pmDisplayAds)) {
      this.includeApsAds();
    }

    if (hydraSavedSearches) {
      if (savedSearchCookie === '1' && isCustomer) {
        removeFromStoredCookies(SAVED_SEARCH_COOKIE);
        saveSearch(location.pathname + location.search);
      }

      toggleSavedSearch(urlMatchesSavedSearch(location, savedSearches));
    }
  }

  componentDidUpdate(prevProps) {
    // Mobile sticky header
    ensureClass(document.body, 'fixedSearchMenu');

    // If search term changes at all, reset the feedback form for another submission
    const { filters: { originalTerm }, location, savedSearches, toggleSavedSearch } = this.props;
    const { filters: { originalTerm: prevOriginalTerm }, hydraSavedSearches, location: oldLocation, savedSearches: oldSavedSearches } = prevProps;
    const locationsDiffer = !deepEqual(location, oldLocation);
    const savedSearchesDiffer = !deepEqual(savedSearches, oldSavedSearches);

    if (originalTerm !== prevOriginalTerm) {
      this.setState({ feedbackSubmitted: false });
    }

    if (hydraSavedSearches && (locationsDiffer || savedSearchesDiffer)) {
      toggleSavedSearch(urlMatchesSavedSearch(location, savedSearches));
    }

    this.plsrSeoRedirect();
  }

  componentWillUnmount() {
    const { isFacetsVisible } = this.props;

    if (isFacetsVisible) {
      if (/\/product\/\d+\/color\/\d+|^\/$/.test(location.pathname)) {
        this.onApplyFilters();
        return false;
      }
    }
  }

  pastSticky = 0;

  plsrSeoRedirect = () => {
    // We want to redirect to plsr=1 when in `crss` test for SEO reasons
    // Note: we only trigger this test assignment if userAgent does NOT contain "bot"
    // https://github01.zappos.net/mweb/marty/issues/8850
    const { location: { search, pathname }, hydraSearchColorSwatches, colorSwatchToggleState } = this.props;
    if (hydraSearchColorSwatches && colorSwatchToggleState && !search.includes('plsr=1')) {
      // Using history.replaceState() instead of redirectTo because we don't want to trigger a
      // URL change and make a redundant call to the search API
      window.history.replaceState(null, null, appendQuery(`${pathname}${search}`, 'plsr=1'));
    }
  };

  includeApsAds() {
    const slots = [
      SRP_WIDE_TOP,
      SRP_WIDE_MID,
      SRP_NARROW_MID,
      SRP_NARROW_TOP
    ];

    fireApsAds({ marketplace, slots });
  }

  onPagination = page => {
    const { trackEvent } = this.props;
    trackEvent('TE_SEARCH_PAGINATION', `${page}`);
  };

  makePagination = () => {
    const { filters } = this.props;
    return filters.pageCount > 1 && <Pagination
      firstPageIndex={0}
      page={filters.page}
      filters={filters}
      compact={false}
      smallerButtons={true}
      totalPages={filters.pageCount}
      onPagination={this.onPagination}
      useSearchPageStyles={true} />;
  };

  searchScrollTop = () => {
    if (window) {
      const _this = this;
      const originalTop = window.scrollY;
      const step = originalTop / 10;
      setTimeout(() => {
        window.scrollTo(0, originalTop - step);
        if (window.scrollY > 0) {
          _this.searchScrollTop();
        }
      }, 10);
    }
  };

  makeSearchFooter = ({ hasProductResults }) => {
    const { filters: { honeTag, page } } = this.props;

    return (
      <>
        <div className={css.searchFooterWrapper} id="searchPagination">
          {this.makePersonalizedSortToggle(true)}
          {this.makePagination()}
          {this.makeSeoCopyBottomPosition()}
          <div className={css.bannerAdContainer}>
            {
              hasProductResults() &&
              <button
                type="button"
                ref={el => this.backToTop = el}
                className={css.backToTop}
                onClick={this.searchScrollTop} />
            }
          </div>
        </div>
        <AdsenseSlot
          className={css.adsenseFooter}
          name="adSearchFooter"
          styleId="2019220181"
          page={page}
          query={honeTag} />
      </>
    );
  };

  makeScrollhandler = () => {
    const { backToTop } = this;
    if (window && document) {
      onEvent(document, 'scroll', () => {
        const scrollTop = window.scrollY;
        if (scrollTop > 200 && !backToTop.classList.contains(css.visible)) {
          backToTop.classList.add(css.visible);
        } else if (scrollTop < 200 && backToTop.classList.contains(css.visible)) {
          backToTop.classList.remove(css.visible);
        }
      }, null, this);
    }
  };

  onSortSelected = ({ target }) => {
    const { filters, trackEvent, cookieDomain, trackLegacyEvent, updateSort, updateBestForYou } = this.props;
    if (target.value === 'bestForYou-desc') {
      setCookie(SEARCH_BEST_FOR_YOU_COOKIE, '', { domain: cookieDomain });
      updateBestForYou(true);
    } else {
      updateBestForYou(false);
    }
    updateSort(sortStringToObject(target.value));
    const payload = buildSearchTermEventPayload(filters.term);
    const { label } = target[target.selectedIndex].dataset;

    const sortByEventInfo = sanitizeForEvent(label);
    trackLegacyEvent('Search-Results-Page', `Sort-By-${sortByEventInfo}`, payload);
    trackEvent('TE_SORTMENU_SELECTSORT', sortByEventInfo);
    track(() => ([
      evSortSearchClick, {
        sortType: label
      }]));
  };

  runToggleFacetsContainer = opening => {
    const { toggleFacetsContainer } = this.props;
    toggleFacetsContainer(opening);
  };

  onApplyFilters = () => {
    const { trackEvent } = this.props;
    this.runToggleFacetsContainer(false);
    trackEvent('TE_SEARCH_APPLYFILTERS');
  };

  makeAccessibilityAnchors = hasProductResults => {
    const { filters: { pageCount, selected: { singleSelects, multiSelects } } } = this.props;
    if (hasProductResults()) {
      const links = {
        searchPage: 'Skip to search results',
        searchFilters: 'Skip to filters',
        searchSort: 'Skip to sort',
        searchPagination: pageCount && pageCount > 1 ? 'Skip to pagination' : null,
        searchSelectedFilters: !!Object.keys(singleSelects).length || !!Object.keys(multiSelects).length ? 'Skip to selected filters' : null
      };
      return <SkipLinks links={links} />;
    }
    return null;
  };

  handlePersonalizedBestForYou = () => {
    const { updateBestForYou, cookieDomain, filters, updateSort } = this.props;
    const isNewBestForYouStateActive = !filters.bestForYou || !filters.sort.bestForYou;
    updateBestForYou(isNewBestForYouStateActive);
    updateSort(isNewBestForYouStateActive ? { bestForYou: 'desc' } : { relevance: 'desc' });
    setCookie(SEARCH_BEST_FOR_YOU_COOKIE, isNewBestForYouStateActive ? '' : 'active', { domain: cookieDomain });
    trackEvent('TE_PERSONALIZED_SEARCH_BFU_BUTTON', `${isNewBestForYouStateActive}`);
  };

  onToggleFacetsContainer = () => {
    const { isFacetsVisible, trackEvent } = this.props;
    const opening = !isFacetsVisible;
    this.runToggleFacetsContainer(opening);

    if (opening) {
      trackEvent('TE_SEARCH_OPENFILTERS');
    } else {
      trackEvent('TE_SEARCH_CLOSEFILTERS');
    }
  };

  makeSeoCopyBottomPosition() {
    return this.makeSeoCopy([SEO_COPY_BOTTOM_COLLAPSED, SEO_COPY_BOTTOM_EXPANDED], css.copyBottom);
  }

  makeSeoCopyTopPosition() {
    return this.makeSeoCopy([SEO_COPY_TOP_COLLAPSED], css.copyTop);
  }

  makeSeoCopy(requiredLocations, positionCSS) {
    const { hydraTestH1TitleCopy, filters: { seoData } } = this.props;

    const copy = seoData?.copy;
    const copyGroup = seoData?.copyGroup;
    if (hydraTestH1TitleCopy && copy && requiredLocations.includes(copyGroup)) {
      const isCollapsed = this.state.seoCopyCollapsed && [SEO_COPY_TOP_COLLAPSED, SEO_COPY_BOTTOM_COLLAPSED].includes(copyGroup);
      const pClassName = cn({ [css.collapsed]: isCollapsed });

      return (
        <div className={cn(css.seoWrapper, positionCSS)}>
          { parseAndCleanHtmlToReact(copy, pClassName) }
          { isCollapsed && <button type="button" className={css.readMore} onClick={this.toggleSeoCopy}>Read More</button> }
        </div>
      );
    }
    return '';
  }

  toggleSeoCopy = () => {
    this.setState({ seoCopyCollapsed: !this.state.seoCopyCollapsed });
  };

  makeSeoText = () => {
    const { filters: { seoText, termLander, originalTerm }, showSeoText } = this.props;
    const { testId } = this.context;

    if (showSeoText && (seoText || (termLander && originalTerm))) {
      return (
        <div className={css.seoWrapper}>
          {seoText && <div className={css.seoText}>
            <p dangerouslySetInnerHTML={{ __html: sanitizer.sanitize(seoText) }} />
          </div>}
          {termLander && originalTerm && <p data-test-id={testId('termLanderMessage')}>We adjusted your search results to be more relevant. <Link to={`/search/null/orig/${originalTerm}`}>Not what you were looking for? Click here.</Link></p>}
        </div>
      );
    }
    return '';
  };

  onSearchLayoutSelected = ({ target }) => {
    const { trackEvent, updateSearchLayout } = this.props,
      newLayout = target.value;
    updateSearchLayout(newLayout);
    trackEvent('TE_LAYOUTMENU_SELECTLAYOUT', newLayout);
  };

  onProductClicked = (productPosition, productId, styleId, e) => {
    const { filters, searchLayout, trackEvent, trackLegacyEvent, isFacetsVisible } = this.props;

    if (isFacetsVisible) {
      this.runToggleFacetsContainer(false);
      e.preventDefault();
    } else {
      const searchTerm = buildSearchTermEventPayload(filters.term);
      trackEvent('TE_SEARCH_CLICKPRODUCT', `${productId}:${styleId}:${productPosition}:${filters.page}`);
      // Duplicate event is expected #8854
      trackEvent('TE_SEARCH_CLICKTHROUGHPRODUCT', `${productId}:${styleId}:${productPosition}:${filters.page}`);
      trackLegacyEvent('Search-Results-Click-Through', formatProductClickEvent(filters.page, productPosition, searchLayout), searchTerm);
      trackLegacyEvent('Search-Results-Page', 'Results', productId);
    }
  };

  makeStickyHeader = () => {
    const { onSortSelected, state: { stickyHeaderTop } } = this;
    const {
      filters,
      isFacetsVisible,
      products,
      searchLayout,
      removePersonalizedSize,
      route: { SearchMenu },
      hydraSavedSearches
    } = this.props;

    const stickyHeaderStyles = { top: `${stickyHeaderTop}px` };

    return (
      <div ref={e => this.stickyHeader = e} className={css.stickyHeader} style={stickyHeaderStyles}>
        <SearchMenu
          key="searchMenu"
          filters={filters}
          isFacetsVisible={isFacetsVisible}
          products={products}
          onSortSelected={onSortSelected}
          onToggleFacetsContainer={this.onToggleFacetsContainer}
          onLayoutSelected={this.onSearchLayoutSelected}
          selectedLayout={searchLayout}
          handleSavedSearch={this.handleSavedSearch}
          hydraSavedSearches={hydraSavedSearches} />
        <DesktopSearchMenu
          key="desktopSearchMenu"
          filters={filters}
          handlePillClick={this.handlePillClick}
          removePersonalizedSize={removePersonalizedSize} />
      </div>
    );
  };

  makeJanusPixel = () => {
    const { filters: { originalTerm }, location } = this.props;
    const queryParams = {
      widget : 'RecordSearch',
      txt : originalTerm || 'no-term'
    };
    return <JanusPixel location={location} queryParams={queryParams}/>;
  };

  onInlineVerticalRecoClick = (index, productId) => {
    const { trackEvent } = this.props;
    trackEvent('TE_SEARCH_INLINEVERTICALRECOS', `${index}:${productId}`);
  };

  onInlineRecoClick = recoName => {
    const { trackEvent } = this.props;
    trackEvent('TE_SEARCH_INLINERECOS', recoName);
  };

  onFacetGroupSelect = event => {
    const { toggleSelectedFacetGroup } = this.props;
    const groupName = event.currentTarget.getAttribute('data-selected-facet-group-name');
    toggleSelectedFacetGroup(groupName);
  };

  makeProducts = ({ sponsoredProducts, hydraInlineBannerAds, hydraBothBannerAds, isCustomer, inlineBannerData } = {}) => {
    const { onProductClicked, onSortSelected, onInlineRecoClick, onInlineVerticalRecoClick, props, state, handleSearchFeedbackClick } = this;
    const {
      colorSwatchToggleState,
      filters,
      getHeartProps,
      hydraCrossSiteSelling,
      hydraNewAds,
      hydraPDPProductAngles,
      hydraSearchColorSwatches,
      hydraVadsNoBox,
      hydraVadsNoBoxTopRow,
      hydraSocialCollections,
      msaImageParams,
      products,
      route: { ProductComponent },
      showRatingStars,
      searchLayout,
      trackSponsoredProductEvent,
      triggerAssignment
    } = props;
    const { feedbackSubmitted } = state;

    const heartsData = getHeartProps({ ...props, hasHearting }, { addedFrom: SEARCH_PAGE, heartEventName: 'TE_SEARCH_PRODUCT_HEART', unHeartEventName: 'TE_SEARCH_PRODUCT_UNHEART' });
    return (
      <>
        <Products
          ProductComponent={ProductComponent}
          products={products}
          filters={filters}
          onSortSelected={onSortSelected}
          page={filters.page}
          onProductClicked={onProductClicked}
          showRatings={showRatings}
          layout={searchLayout}
          heartsData={heartsData}
          hydraVadsNoBox={hydraVadsNoBox}
          hydraVadsNoBoxTopRow={hydraVadsNoBoxTopRow}
          onInlineRecoClick={onInlineRecoClick}
          onInlineVerticalRecoClick={onInlineVerticalRecoClick}
          msaImageParams={msaImageParams}
          showRatingStars={showRatingStars}
          colorSwatchToggleState={colorSwatchToggleState}
          hydraNewAds={hydraNewAds}
          hydraPDPProductAngles={hydraPDPProductAngles}
          hydraSocialCollections={hydraSocialCollections}
          hydraSearchColorSwatches={hydraSearchColorSwatches}
          feedbackSubmitted={feedbackSubmitted}
          handleSearchFeedbackClick={handleSearchFeedbackClick}
          sponsoredProducts={sponsoredProducts}
          inlineBannerData={inlineBannerData}
          hydraInlineBannerAds={hydraInlineBannerAds}
          hydraBothBannerAds={hydraBothBannerAds}
          isCustomer={isCustomer}
          hydraCrossSiteSelling={hydraCrossSiteSelling}
          showLoader={true}
          trackSponsoredProductEvent={trackSponsoredProductEvent}
          triggerAssignment={triggerAssignment}
        />
      </>
    );
  };

  clearFacetGroup = () => {
    const { resetFacetGroup, facets } = this.props;
    const facetField = (hasFacetListClear && facets.chosenFacetGroup) ? facets.chosenFacetGroup.facetField : null;
    resetFacetGroup(facetField);
  };

  handlePillClick = ({ name, value }, index) => {
    this.onFacetSelect(name, value, null, index);
  };

  clearChosenFacet = () => {
    const { toggleFacetChosen } = this.props;
    toggleFacetChosen(null);
    this.facets.scrollTop = 0;
  };

  facetDone = () => {
    this.runToggleFacetsContainer(false);
    this.clearChosenFacet();
  };

  saveFiltersRedirectToLogin = () => {
    const { cookieDomain, location, redirectToAuthenticationFor, filters } = this.props;
    const canSave = formatSavedFilters(filters);
    if (canSave) {
      setCookie(SAVI_SIZE_COOKIE, '1', { domain: cookieDomain });
    }
    redirectToAuthenticationFor(location);
  };

  onSaveSizeClick = () => {
    const { saveFilters, isCustomer } = this.props;
    if (!isCustomer) {
      this.saveFiltersRedirectToLogin();
    } else {
      saveFilters();
      trackEvent('TE_SAVED_FILTERS_SAVE_CLICK');
    }
  };

  onResetSizeClick = facetField => {
    const { deleteSavedFilters, filters: { savedsizes }, saveFilters } = this.props;
    const savedFiltersWithValues = Object.values(savedsizes.filters).filter(v => v.length);
    const savedFilter = savedsizes.filters[facetField] || [];
    if (savedFilter.length) {
      if (savedFiltersWithValues.length > 1) {
        saveFilters(facetField);
      } else {
        if (savedsizes?.id) {
          deleteSavedFilters(savedsizes.id);
        }
      }
      trackEvent('TE_SAVED_FILTERS_RESET_CLICK');
    }
  };

  onSaveFeatureToggle = () => {
    const { cookieDomain, isCustomer, toggleSavedFilters, filters: { applySavedFilters } } = this.props;
    if (!isCustomer) {
      this.saveFiltersRedirectToLogin();
    } else {
      if (applySavedFilters) {
        trackEvent('TE_SAVED_FILTERS_TOGGLE_OFF');
      }
      setCookie(SAVI_COOKIE, applySavedFilters ? 'true' : '', { domain: cookieDomain });
      toggleSavedFilters();
    }
  };

  onFacetSelect = (facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex, isPcm) => {
    const { filters, facets, togglePersonalizedSize } = this.props;
    if (facetGroup === BEST_FOR_YOU_FACETFIELD) {
      togglePersonalizedSize(facets);
      trackEvent('TE_PERSONALIZED_SEARCH_SIZE', `${!filters.personalizedSize?.facets?.[0]?.selected}`);
    } else {
      const { facets, filters, toggleSelectedFacet, trackEvent, trackLegacyEvent, clearAutoComplete } = this.props;

      if (filters.personalizedSize?.facets?.[0]?.selected && (filters.personalizedSize?.sizes.indexOf(facetName) > -1)) {
        togglePersonalizedSize();
      }
      toggleSelectedFacet(facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex, isPcm);
      clearAutoComplete(facetGroup);
      const facetLocation = isPcm ? 'pcm' : 'toDisplay';
      const facet = facets[facetLocation][selectedFacetGroupIndex];

      if (facet) {
        const facetGroupDisplayName = sanitizeForEvent(facet.facetFieldDisplayName);
        const sanitizedName = sanitizeForEvent(facetName);
        const searchTerm = buildSearchTermEventPayload(filters.term);
        trackLegacyEvent('Search-Results-Page', `Facet-Click-${facetGroupDisplayName}-${sanitizedName}`, searchTerm);
        trackLegacyEvent('Search-Results-Page', `FCT${facetGroup.toLowerCase()}`, sanitizedName);
        trackEvent('TE_SEARCH_FILTERS', `${facetGroupDisplayName}:${facetName}${isPcm ? ':pcm:true' : ''}`);
      }
    }
  };

  handleSearchFeedbackClick = feedback => {
    this.setState({ feedbackSubmitted: true });
    this.props.searchFeedbackClick(feedback);
  };

  makePersonalizedMessage = () => {
    const { testId } = this.context;
    const { filters: { sort, bestForYouSortEligible } } = this.props;
    if (bestForYouSortEligible) {
      return (
        <>
          <Tooltip
            tooltipId="personalizedSearch"
            wrapperClassName={css.tooltipWrapper}
            tooltipClassName={css.tooltip}
            direction="left"
            content={'Your own special blend is here! We\'re combining your feedback, recent purchases, and shopping behavior to sort your best results to the top.'}>
            <MoreInfo/>
          </Tooltip>
          <span>
            Results are {(!sort.bestForYou) ? 'not' : ''} sorted based on your Preferences.{' '}
            <button
              type="button"
              onClick={this.handlePersonalizedBestForYou}
              data-test-id={testId('bestForYouButton')}
              aria-label={`Turn ${sort.bestForYou ? 'off' : 'on'} best for you sort.`}
            >
              Turn {sort.bestForYou ? 'off' : 'on'}
            </button>
          </span>
        </>
      );
    }
  };

  makePersonalizedSortToggle = (isFooter = false) => {
    const { testId } = this.context;
    return (
      <div data-test-id={testId('bestForYouMessaging')} className={cn(css.hpsMessage, { [css.footer]: isFooter })}>
        {this.makePersonalizedMessage()}
      </div>
    );
  };

  handleSavedSearch = () => {
    const { isCustomer, saveSearch, location, products: { urlMatchesSavedSearch }, redirectToAuthenticationFor, deleteSavedSearch } = this.props;
    if (isCustomer) {
      const fullUrl = location.pathname + location.search;
      if (urlMatchesSavedSearch) {
        deleteSavedSearch(urlMatchesSavedSearch.id);
      } else {
        saveSearch(fullUrl);
      }
    } else {
      redirectToAuthenticationFor(location);
      setCookie(SAVED_SEARCH_COOKIE, '1', { domain: cookieDomain });
    }
  };

  render() {
    const { autoComplete, colorSwatchToggleState, facets, filters, filters: { breadcrumbs }, hydraSearchColorSwatches, isFacetsVisible, location, onApplyFilters, params, products, setLayoutOption, symphonyData, isLoadingSymphony, hydraSaveFilters, isCustomer, hydraSavedSearches } = this.props;
    const { testId } = this.context;
    const isLoading = !!products.isLoading;
    const topBannerData = symphonyData?.slotData?.['search-header-1'];
    const inlineBannerData = symphonyData?.slotData?.['search-results-1'];
    return (
      <SiteAwareMetadata loading={isLoading || !products.executedSearchUrl}>
        <SearchLogic
          makeScrollhandler={this.makeScrollhandler}
          location={location}
          params={params}
          hydraSearchColorSwatches={hydraSearchColorSwatches}
          hydraSavedSearches={hydraSavedSearches}
          setLayoutOption={setLayoutOption}>
          {({
            handleColorSwatchToggle,
            hasProductResults,
            shouldShowNoResults,
            sponsoredProducts,
            hydraTopBannerAds,
            hydraInlineBannerAds,
            hydraBothBannerAds
          }) => (
            <>
              <div ref={e => this.stickyPlaceholder = e} className={cn(css.wrap, 'searchWrapper')}>
                {!shouldShowNoResults() &&
                  <div>
                    {this.makeAccessibilityAnchors(hasProductResults)}
                    <GamSlot slot={SRP_WIDE_TOP} className={css.displayAd} />
                    <GamSlot slot={SRP_NARROW_TOP} />
                    <DesktopSearchHeader
                      totalProductCount={products.totalProductCount}
                      onSortSelected={this.onSortSelected}
                      filters={filters}
                      products={products}
                      hydraSavedSearches={hydraSavedSearches}
                      handleSavedSearch={this.handleSavedSearch}
                      makePersonalizedSortToggle={this.makePersonalizedSortToggle}
                    />
                    { (hydraTopBannerAds || hydraBothBannerAds) && !isLoadingSymphony && !breadcrumbs.length && <SearchSlot slotDetails={topBannerData} searchTerm={filters.term} />}
                    {this.makeStickyHeader()}
                    {this.makeSeoCopyTopPosition()}
                    {this.makeSeoText()}
                    {this.makeProducts({ sponsoredProducts, hydraInlineBannerAds, hydraBothBannerAds, isCustomer, inlineBannerData })}
                    <div className={css.toggleBottomFacets}>
                      <button type="button" onClick={this.onToggleFacetsContainer}>Filter & Sort</button>
                    </div>
                    <div className={cn({ [css.modalOverlay]: isFacetsVisible })}>
                      <aside className="facetWrapper facets" id="searchFilters">
                        <div
                          ref={c => this.facets = c}
                          data-test-id={testId('facets')}>
                          <FacetMenu
                            clearChosenFacet={this.clearChosenFacet}
                            totalProductCount={products.totalProductCount}
                            facets={facets}
                            hasMobileLayeredFacets={true}
                            mobileFacetHeader={mobileFacetHeader} />
                          <Facets
                            autoComplete={autoComplete}
                            autoCompleteMinValues={autoCompleteMinValues}
                            onApplyFilters={onApplyFilters}
                            isFacetsVisible={isFacetsVisible}
                            filters={filters}
                            facets={facets}
                            onFacetSelect={this.onFacetSelect}
                            onFacetGroupSelect={this.onFacetGroupSelect}
                            clearFacetGroup={this.clearFacetGroup}
                            runToggleFacetsContainer={this.runToggleFacetsContainer}
                            facetDone={this.facetDone}
                            hydraSearchColorSwatches={hydraSearchColorSwatches}
                            handleColorSwatchToggle={handleColorSwatchToggle}
                            colorSwatchToggleState={colorSwatchToggleState}
                            facetHeader={facetHeader}
                            hasMultiSelectMessaging={true}
                            sortOptions={sortOptions}
                            onSortSelected={this.onSortSelected}
                            hasAutoComplete={true}
                            hydraSaveFilters={hydraSaveFilters}
                            isCustomer={isCustomer}
                            onSaveSizeClick={this.onSaveSizeClick}
                            onResetSizeClick={this.onResetSizeClick}
                            saveFilters={saveFilters}
                            onSaveFeatureToggle={this.onSaveFeatureToggle} />
                          <FacetActions
                            clearFacetGroup={this.clearFacetGroup}
                            isFacetsVisible={isFacetsVisible}
                            onApplyFilters={onApplyFilters}
                            selectedFacet={facets.chosenFacetGroup}
                            facetDone={this.facetDone}
                            mobileCloseFiltersText={mobileCloseFiltersText}
                            mobileRemoveSelectionsText={mobileRemoveSelectionsText} />
                        </div>
                        <GamSlot slot={SRP_WIDE_MID} />
                      </aside>
                    </div>
                    {this.makeJanusPixel()}
                    {this.makeSearchFooter({ hasProductResults })}
                  </div>
                }
              </div>
            </>
          )}
        </SearchLogic>
      </SiteAwareMetadata>
    );
  }
}

StandardSearch.defaultProps = {
  trackEvent,
  trackLegacyEvent
};

StandardSearch.contextTypes = {
  router: PropTypes.object.isRequired,
  testId: PropTypes.func.isRequired,
  marketplace: PropTypes.object.isRequired
};

StandardSearch.propTypes = {
  products: PropTypes.object.isRequired,
  filters: PropTypes.object,
  facets: PropTypes.object
};

export function computeSearchLayout(state, validLayout = isValidLayout) {
  if (state.searchLayout) {
    return state.searchLayout;
  }

  return validLayout(state.cookies[SEARCH_LAYOUT_COOKIE]) ? state.cookies[SEARCH_LAYOUT_COOKIE] : 'three-item';
}

function mapStateToProps(state) {
  const { headerFooter: { savedSearches }, autoComplete, cookies, facets, filters,
    hearts: { heartsStyleIds, heartLoginPrompt }, isFacetsVisible, search: { isLoadingSymphony, symphony },
    products, url: { userAgent } } = state;
  const searchLayout = computeSearchLayout(state);

  const isCustomer = state.cookies['x-main'];

  return {
    autoComplete,
    cookieDomain,
    colorSwatchToggleState: cookies[SEARCH_COLOR_SWATCHES_COOKIE] !== 'off',
    facets,
    filters,
    getHeartProps,
    hearts: heartsStyleIds,
    heartLoginPrompt,
    hydraNewAds: isAssigned(HYDRA_NEW_ADS, 1, state),
    hydraVadsNoBox: isAssigned(HYDRA_VISUALLY_ADJUSTED_ADS, 1, state),
    hydraVadsNoBoxTopRow: isAssigned(HYDRA_VISUALLY_ADJUSTED_ADS, 2, state),
    hydraPDPProductAngles: getAssignmentGroup(HYDRA_PDP_PRODUCT_ANGLES, state),
    hydraSearchColorSwatches: getAssignmentGroup(HYDRA_SEARCH_COLOR_SWATCHES, state),
    hydraSocialCollections: isAssigned(HYDRA_SOCIAL_COLLECTIONS, 1, state),
    hydraTestH1TitleCopy: isAssigned(HYDRA_SEO_H1_TITLE_COPY, 1, state),
    hydraSaveFilters: isAssigned(HYDRA_SAVE_AND_APPLY_FILTERS, 1, state),
    hydraSavedSearches: isAssigned(HYDRA_SAVE_SEARCHES, 1, state),
    hydraCrossSiteSelling: isAssigned(HYDRA_CROSS_SITE_SELLING, 1, state),
    isFacetsVisible,
    isCustomer,
    isLoadingSymphony,
    msaImageParams: msaMelodyImageParams,
    products,
    savedSearches,
    savedSearchCookie: cookies[SAVED_SEARCH_COOKIE] || null,
    saviSizeCookie: cookies[SAVI_SIZE_COOKIE] || null,
    showRatingStars,
    showSeoText,
    searchLayout,
    symphonyData: symphony,
    userAgent
  };
}

export const mapDispatchToProps = {
  clearAutoComplete,
  deleteSavedSearch,
  deleteSavedFilters,
  heartProduct,
  redirectToAuthenticationFor,
  removeFromStoredCookies,
  removePersonalizedSize,
  resetFacetGroup,
  saveSearch,
  toggleHeartingLoginModal,
  toggleFacetChosen,
  toggleFacetsContainer,
  togglePersonalizedSize,
  toggleSelectedFacet,
  toggleSelectedFacetGroup,
  trackSponsoredProductEvent,
  triggerAssignment,
  updateBestForYou,
  updateSearchLayout,
  unHeartProduct,
  updateSort,
  searchFeedbackClick,
  saveFilters,
  toggleSavedFilters,
  toggleSavedSearch
};

export default connect(mapStateToProps, mapDispatchToProps)(StandardSearch);
